<template>
  <global-dialog-card
    v-model="model"
    :max-width="1200"
    hide-padding
    :title="$t('menu_selection_description')"
  >
    <v-container fluid class="grey lighten-5 pa-6">
      <v-row class="mt-4">
        <v-col
          v-for="(check, index) in menuItems"
          :key="index"
          cols="12"
          sm="6"
          md="4"
          xl="3"
        >
          <v-card class="pa-4 permission-card" outlined :hover="true">
            <v-checkbox
              v-if="check.title == 'restaurants' && employee"
              v-model="selectedMenuItems"
              :label="$t('restaurants')"
              :value="check.title"
              @change="handleRestaurantChange(employee)"
              :disabled="restaurantLoading"
              color="primary"
              hide-details
              class="ma-0"
            ></v-checkbox>
            <v-checkbox
              v-else-if="check.title == 'settings' && employee"
              v-model="employee.pivot.settings"
              :label="$t('settings')"
              :value="employee.pivot.settings"
              @change="setPodesavanja(employee)"
              :disabled="settingsLoading"
              color="primary"
              hide-details
              class="ma-0"
            ></v-checkbox>
            <v-checkbox
              v-else
              multiple
              v-model="selectedMenuItems"
              :label="$t(check.title)"
              :value="check.title"
              color="primary"
              hide-details
              class="ma-0"
            ></v-checkbox>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <template #actions>
      <v-btn text @click="model = false">
        {{ $t("cancel") }}
      </v-btn>
      <primary-button @click="storeUserMenu()" :loading="loading">
        {{ $t("save") }}
      </primary-button>
    </template>
  </global-dialog-card>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasSnackbar from "@/mixins/hasSnackbar";
import userService from "@/services/user";

export default {
  mixins: [hasModel, hasSnackbar],
  props: {
    employee: {
      type: Object,
      default: () => ({}),
    },
    podesavanja: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    selectedMenuItems: [],
    loading: false,
    settingsLoading: false,
    restaurantLoading: false,
    menuItems: [
      { title: "meal_main" },
      { title: "report" },
      { title: "guest_report" },
      { title: "post_report" },
      { title: "stock" },
      { title: "companies" },
      { title: "companies_delivery" },
      { title: "suppliers" },
      { title: "meals_main" },
      { title: "groups" },
      { title: "menu_types" },
      { title: "groceri" },
      { title: "settings" },
      { title: "gallery" },
      { title: "restaurants" },
      { title: "users" },
      { title: "customers_restoran" },
      { title: "history" },
      { title: "notifications" },
      { title: "import_user" },
      { title: "recipes" },
    ],
    selectedMenuItemsOriginal: [],
    // Menu priority configuration
    menuPriorities: [
      "meal_main",
      "report",
      "guest_report",
      "companies",
      "stock",
      "meals_main",
      "groups",
      "menu_types",
      "groceri",
      "gallery",
      "restaurants",
    ],
  }),

  computed: {
    userMenuIndex() {
      return this.employee?.id || 0;
    },
  },

  mounted() {
    // Initialize all available menu items as default selection
    this.selectedMenuItemsOriginal = this.menuItems.map((item) => item.title);
  },

  methods: {
    async setPodesavanja(item) {
      this.settingsLoading = true;
      try {
        await userService.updateUserSettingsV2({
          user_id: this.employee.id,
          restaurant_id: this.employee.pivot.restaurant_id,
          settings: 1,
        });
        // await userService.updateUserSettings(item.id, item.pivot?.settings);
        this.showSuccessSnackbar(this.$t("settings_updated"));
      } catch (error) {
        console.error("Error updating settings:", error);
        this.showErrorSnackbar(this.$t("error_updating_settings"));
        // Revert settings change on error
        item.pivot.settings = !item.pivot.settings;
      } finally {
        this.settingsLoading = false;
      }
    },

    async handleRestaurantChange(employee) {
      this.restaurantLoading = true;
      try {
        const hasRestaurants = this.selectedMenuItems.includes("restaurants");
        await userService.updateUserRestaurantAccess(employee.id, hasRestaurants);
        this.showSuccessSnackbar(this.$t("restaurant_access_updated"));
      } catch (error) {
        console.error("Error updating restaurant access:", error);
        this.showErrorSnackbar(this.$t("error_updating_restaurant_access"));
        // Revert checkbox state on error
        if (this.selectedMenuItems.includes("restaurants")) {
          this.selectedMenuItems = this.selectedMenuItems.filter(
            (item) => item !== "restaurants"
          );
        } else {
          this.selectedMenuItems.push("restaurants");
        }
      } finally {
        this.restaurantLoading = false;
      }
    },

    // Simplified and more efficient sorting function
    sortMenuItems(items) {
      return items.sort((a, b) => {
        const priorityA = this.menuPriorities.indexOf(a);
        const priorityB = this.menuPriorities.indexOf(b);

        // If both items have priorities, sort by priority
        if (priorityA !== -1 && priorityB !== -1) {
          return priorityA - priorityB;
        }
        // Items with priority come first
        if (priorityA !== -1) return -1;
        if (priorityB !== -1) return 1;

        // Fallback to alphabetical sorting
        return a.localeCompare(b);
      });
    },

    async storeUserMenu() {
      if (!this.employee?.id) return;

      this.loading = true;
      try {
        // Sort menu items efficiently
        const sortedMenuItems = this.sortMenuItems([...this.selectedMenuItems]);

        await userService.updateUserMenu(this.employee.id, sortedMenuItems);

        this.showSuccessSnackbar(this.$t("user_menu_updated"));
        this.model = false;
      } catch (error) {
        console.error("Error saving menu:", error);
        this.showErrorSnackbar(this.$t("error_updating_user_menu"));
      } finally {
        this.loading = false;
      }
    },

    // Helper method to safely parse user menu
    parseUserMenu(menuString) {
      try {
        return JSON.parse(menuString);
      } catch (error) {
        console.error("Error parsing user menu:", error);
        return [];
      }
    },
  },

  watch: {
    employee: {
      handler(employee) {
        if (!employee) return;

        this.selectedMenuItems = [];
        if (employee.user_menu?.menu1) {
          const userMenu = this.parseUserMenu(employee.user_menu.menu1);
          userMenu.forEach((item) => {
            if (item.title) {
              this.selectedMenuItems.push(item.title);
            }
          });
        } else {
          this.selectedMenuItems = [...this.selectedMenuItemsOriginal];
        }
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
/* Permission card styling for better visibility */
.permission-card {
  transition: all 0.2s ease !important;
  border: 1px solid #e0e0e0 !important;
  background: white !important;
}

.permission-card:hover {
  border-color: #1976d2 !important;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.15) !important;
  transform: translateY(-2px);
}

/* Focus state for better accessibility */
.permission-card:has(.v-input--is-focused) {
  border-color: #1976d2 !important;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.1) !important;
}

/* Fine-tune checkbox styling */
.permission-card ::v-deep .v-input--selection-controls__input {
  margin-right: 12px;
}

.permission-card ::v-deep .v-label {
  color: #424242 !important;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.4;
}

.permission-card ::v-deep .v-input--is-disabled .v-label {
  color: rgba(0, 0, 0, 0.38) !important;
}

/* Container background styling */
.grey.lighten-5 {
  background-color: #fafafa !important;
}
</style>
